import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../utils/api';
import type { RaffleWithDetails, Participant, Winner } from '../types';
import PrizeWheel from '../components/PrizeWheel';
import { QRCodeSVG } from 'qrcode.react';

function PresentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [raffle, setRaffle] = useState<RaffleWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [spinning, setSpinning] = useState(false);
  const [pendingWinner, setPendingWinner] = useState<Participant | null>(null);
  const [showWinnerReveal, setShowWinnerReveal] = useState(false);
  const [currentWinner, setCurrentWinner] = useState<Participant | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [prizeName, setPrizeName] = useState('');

  // Track if we're in the middle of confirming a winner
  const confirmingRef = useRef(false);

  const loadRaffle = useCallback(async () => {
    if (!id) return;
    try {
      const data = await adminApi.getRaffle(id);
      setRaffle(data);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load raffle');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadRaffle();
    // Poll for updates every 3 seconds
    const interval = setInterval(loadRaffle, 3000);
    return () => clearInterval(interval);
  }, [loadRaffle]);

  const handlePickWinner = async () => {
    if (!id || !raffle) return;

    // Prevent duplicate spins - check if already spinning
    if (raffle.status === 'spinning' || spinning) {
      setError('A spin is already in progress');
      return;
    }

    const winnerIds = new Set(raffle.winners.map((w: Winner) => w.participant_id));
    const eligibleCount = raffle.participants.filter((p: Participant) => !winnerIds.has(p.id)).length;

    if (eligibleCount === 0) {
      setError('No eligible participants remaining');
      return;
    }

    setCurrentWinner(null);
    setPendingWinner(null);
    setShowWinnerReveal(false);
    setShowConfetti(false);
    setError('');

    try {
      const result = await adminApi.startSpin(id, prizeName || undefined);
      setPendingWinner(result.winner.participant);
      setSpinning(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start spin');
    }
  };

  const handleSpinComplete = async () => {
    if (!id || confirmingRef.current) return;
    confirmingRef.current = true;

    setSpinning(false);

    try {
      await adminApi.confirmWinner(id);

      if (pendingWinner) {
        setCurrentWinner(pendingWinner);
        setShowWinnerReveal(true);
        setShowConfetti(true);
      }

      await loadRaffle();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to confirm winner');
      loadRaffle();
    } finally {
      confirmingRef.current = false;
    }
  };

  const handleDismissWinner = () => {
    setShowWinnerReveal(false);
    setCurrentWinner(null);
    setPendingWinner(null);
    setShowConfetti(false);
    setPrizeName('');
  };

  // Handle escape key to go back
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        navigate(`/admin/raffle/${id}`);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="present-page">
        <div className="loading">Loading raffle...</div>
      </div>
    );
  }

  if (!raffle) {
    return (
      <div className="present-page">
        <div className="error-message">Raffle not found</div>
        <button className="btn btn-secondary" onClick={() => navigate('/admin')}>
          Back to Admin
        </button>
      </div>
    );
  }

  const winnerIds = new Set(raffle.winners.map((w: Winner) => w.participant_id));
  const eligibleParticipants = raffle.participants.filter((p: Participant) => !winnerIds.has(p.id));
  const maxWinnersReached = raffle.winners.length >= raffle.max_winners;

  return (
    <div className="present-page">
      {/* Confetti overlay for winners */}
      {showConfetti && (
        <div className="confetti-container">
          {[...Array(150)].map((_, i) => (
            <div
              key={i}
              className="confetti"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                backgroundColor: ['#1BBC9B', '#0490DA', '#FFC700', '#50CD89', '#F1416C', '#A091FE'][Math.floor(Math.random() * 6)],
              }}
            />
          ))}
        </div>
      )}

      {/* Top Bar */}
      <div className="present-header">
        <div className="present-header-left">
          <button
            className="present-back-btn"
            onClick={() => navigate(`/admin/raffle/${id}`)}
            title="Back to raffle management"
          >
            ←
          </button>
          <h1>{raffle.name}</h1>
          {raffle.status === 'active' && !showWinnerReveal && (
            <span className="entry-code entry-code--header">{raffle.entry_code}</span>
          )}
          <span className={`status-badge status-${raffle.status}`}>{raffle.status}</span>
        </div>
        <div className="present-header-right">
          {!showWinnerReveal && !maxWinnersReached && eligibleParticipants.length > 0 && (
            <div className="present-prize-input">
              <input
                type="text"
                className="form-input"
                value={prizeName}
                onChange={e => setPrizeName(e.target.value)}
                placeholder="Prize name (optional)"
              />
            </div>
          )}
          {!showWinnerReveal && !maxWinnersReached && eligibleParticipants.length > 0 && (
            <button
              className="btn btn-action btn-large"
              onClick={handlePickWinner}
              disabled={spinning || raffle.status === 'spinning'}
            >
              {spinning || raffle.status === 'spinning' ? 'Spinning...' : 'Spin the Wheel'}
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message present-error">{error}</div>}

      {/* Main Content */}
      <div className="present-content">
        {/* Left Sidebar - Participants */}
        <div className="present-sidebar">
          <div className="present-sidebar-section">
            <h3>Participants ({raffle.participants.length})</h3>
            <div className="present-participants-list">
              {raffle.participants.map((p: Participant) => (
                <div
                  key={p.id}
                  className={`present-participant ${winnerIds.has(p.id) ? 'winner' : ''}`}
                >
                  <span className="present-participant-name">
                    {p.first_name} {p.last_name}
                  </span>
                  {winnerIds.has(p.id) && <span className="present-winner-badge">Winner</span>}
                </div>
              ))}
              {raffle.participants.length === 0 && (
                <p className="text-muted">No participants yet</p>
              )}
            </div>
          </div>

          {raffle.winners.length > 0 && (
            <div className="present-sidebar-section present-winners-section">
              <h3 className="accent-warning">Winners ({raffle.winners.length}/{raffle.max_winners})</h3>
              <div className="present-winners-list">
                {raffle.winners.map((w: Winner, index: number) => (
                  <div key={w.id} className="present-winner-item">
                    <span className="present-winner-number">#{index + 1}</span>
                    <span className="present-winner-name">{w.first_name} {w.last_name}</span>
                    {w.prize_name && <span className="present-winner-prize">{w.prize_name}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Main Area - Wheel */}
        <div className="present-main">
          {showWinnerReveal && currentWinner ? (
            <div className="present-winner-reveal">
              <div className="winner-reveal winner-is-me">
                <div className="winner-reveal-icon">🎉</div>
                <h2>We Have a Winner!</h2>
                <div className="winner-reveal-name">
                  {currentWinner.first_name} {currentWinner.last_name}
                </div>
                {prizeName && (
                  <div className="winner-reveal-prize">Won: {prizeName}</div>
                )}
                <button
                  className="btn btn-action btn-large"
                  onClick={handleDismissWinner}
                  style={{ marginTop: '2rem' }}
                >
                  Continue
                </button>
              </div>
            </div>
          ) : maxWinnersReached ? (
            <div className="present-complete">
              <div className="winner-announcement">
                <h2>All Winners Selected!</h2>
                <p className="text-muted">
                  {raffle.max_winners} winner{raffle.max_winners !== 1 ? 's have' : ' has'} been chosen
                </p>
                <div className="winners-list">
                  {raffle.winners.map((w: Winner, index: number) => (
                    <div key={w.id} className="winner-item">
                      <span className="winner-number">#{index + 1}</span>
                      <span className="winner-name">{w.first_name} {w.last_name}</span>
                      {w.prize_name && <span className="winner-prize">{w.prize_name}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : eligibleParticipants.length === 0 ? (
            <div className="present-waiting">
              <h2>Waiting for Participants</h2>
              <p className="text-muted">
                Share the entry code to let people join
              </p>
            </div>
          ) : (
            <div className="present-wheel-container">
              <PrizeWheel
                participants={eligibleParticipants}
                spinning={spinning}
                winner={pendingWinner}
                onSpinComplete={handleSpinComplete}
                maxSize={600}
              />
              <div className="present-wheel-info">
                <p>{eligibleParticipants.length} eligible participant{eligibleParticipants.length !== 1 ? 's' : ''}</p>
                <p className="text-muted">
                  {raffle.max_winners - raffle.winners.length} winner{raffle.max_winners - raffle.winners.length !== 1 ? 's' : ''} remaining
                </p>
              </div>
            </div>
          )}

          {/* QR Code - bottom-right corner */}
          {raffle.status === 'active' && !showWinnerReveal && (
            <div className="present-qr-code">
              <QRCodeSVG
                value={`${window.location.origin}/join?code=${raffle.entry_code}`}
                size={90}
                bgColor="#FFFFFF"
                fgColor="#151521"
                level="M"
              />
              <span className="present-qr-code__label">Scan to Join</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PresentPage;
